import { UploadFile } from 'antd/lib/upload/interface';
import { AxiosRequestConfig } from 'axios';
import { useCallback } from 'react';
import {
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { FileAttachment, FileAttachmentV2 } from 'api/attachments';
import {
  FileGroupSource,
  FileGroupSourceType,
  FilesAPI,
  RearrangeFilesPayload,
  RearrangeFoldersPayload,
  UploadFilePayload,
} from 'api/files';
import { FileGroup } from 'api/files/types';
import { CreateGroupFormProps } from 'components/organisms/files/create-group/modal';
import { UpdateGroupFormProps } from 'components/organisms/files/update-group/modal';
import { GENERAL_API_STALE } from 'config/query-config';

type FileGroupsQueryKeyType = ['file-groups', 'by-source', string, number];
export const getFileGroupsQueryKey = (
  source: FileGroupSource,
): FileGroupsQueryKeyType => [
  'file-groups',
  'by-source',
  source.sourceType,
  source.sourceId,
];
export const useFileGroupsQuery = (
  source: FileGroupSource,
): UseQueryResult<FileGroup[]> =>
  useQuery(getFileGroupsQueryKey(source), () => FilesAPI.getFileGroups(source));

type FileGroupsForProjectsQueryKeyType = ['file-groups', 'projectIds', string];
export const getFileGroupsForProjectsQueryKey = (
  projectIds: number[],
): FileGroupsForProjectsQueryKeyType => [
  'file-groups',
  'projectIds',
  projectIds.join(','),
];
export const useFileGroupsForProjectsQuery = (
  projectIds: number[],
  options: UseQueryOptions<
    Record<number, FileGroup[]>,
    unknown,
    Record<number, FileGroup[]>,
    FileGroupsForProjectsQueryKeyType
  > = {},
): UseQueryResult<Record<number, FileGroup[]>> =>
  useQuery(
    getFileGroupsForProjectsQueryKey(projectIds),
    () => FilesAPI.getBulkProjectFileGroups(projectIds),
    {
      staleTime: GENERAL_API_STALE,
      ...options,
    },
  );

type FileAttachementsForProjectsQueryKeyType = [
  'attachments',
  'projectIds',
  string,
];
export const getFileAttachementsForProjectsQueryKey = (
  projectIds: number[],
): FileAttachementsForProjectsQueryKeyType => [
  'attachments',
  'projectIds',
  projectIds.join(','),
];
export const useFileAttachementsProjectsQuery = (
  projectIds: number[],
  options: UseQueryOptions<
    Record<number, FileAttachmentV2[]>,
    unknown,
    Record<number, FileAttachmentV2[]>,
    FileAttachementsForProjectsQueryKeyType
  > = {},
): UseQueryResult<Record<number, FileAttachmentV2[]>> =>
  useQuery(
    getFileAttachementsForProjectsQueryKey(projectIds),
    () => FilesAPI.getBulkProjectAttachments(projectIds),
    {
      staleTime: GENERAL_API_STALE,
      ...options,
    },
  );

export const useInvalidateProjectFiles = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (projectId: number) => {
      await queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (queryKey.length !== 3) {
            return false;
          }
          if (
            (queryKey[0] === 'file-groups' || queryKey[0] === 'attachments') &&
            queryKey[1] === 'projectIds' &&
            (queryKey[2] as string).includes(projectId.toString())
          ) {
            return true;
          }

          return false;
        },
      });
    },
    [queryClient],
  );
};

type CreateFileGroupPayload = CreateGroupFormProps & FileGroupSource;

export const useCreateFileGroupMutation = (
  options: UseMutationOptions<FileGroup, unknown, CreateFileGroupPayload> = {},
) =>
  useMutation(
    ({ groupName, permission, sourceId, sourceType }: CreateFileGroupPayload) =>
      FilesAPI.createFileGroup({
        folderName: groupName,
        isPrivate: permission === 'private',
        sourceId,
        sourceType,
      }),
    options,
  );

type UploadFileVariables = {
  file: UploadFile;
  data: UploadFilePayload;
  reqConfig: AxiosRequestConfig;
};
export const useUploadFileMutation = (
  options: UseMutationOptions<
    FileAttachment,
    unknown,
    UploadFileVariables
  > = {},
) => {
  return useMutation(
    ({ file, data, reqConfig }) => FilesAPI.uploadFile(file, data, reqConfig),
    options,
  );
};

type LinkFileVariables = {
  fileIds: number[];
  groupId: number;
  sourceType: FileGroupSourceType;
  sourceId: number;
  reqConfig?: AxiosRequestConfig;
};
export const useLinkFilesToProjectsMutation = (
  options: UseMutationOptions<UploadFile, unknown, LinkFileVariables> = {},
) => {
  return useMutation(
    ({ groupId, fileIds, sourceType, sourceId, reqConfig }) =>
      FilesAPI.linkFiles(groupId, fileIds, sourceType, sourceId, reqConfig),
    options,
  );
};

type DeleteFileVariables = {
  fileId: number;
  groupId: number;
  companyId: number;
};

export const useDeleteFileMutation = (
  options: UseMutationOptions<void, unknown, DeleteFileVariables> = {},
) => {
  return useMutation(
    ({ groupId, fileId, companyId }) =>
      FilesAPI.deleteFile(groupId, fileId, companyId),
    options,
  );
};
type RearrangeFilesVariables = {
  sourceType: string;
  sourceId: number;
  payload: RearrangeFilesPayload;
};

export const useRearrangeFilesMutation = (
  options: UseMutationOptions<void, unknown, RearrangeFilesVariables> = {},
) =>
  useMutation(
    ({ sourceType, sourceId, payload }) =>
      FilesAPI.rearrangeFiles(sourceType, sourceId, payload),
    options,
  );

type RearrangeFolderForCompanyVariables = {
  payload: RearrangeFoldersPayload;
  companyId: number;
};

export const useRearrangeFoldersMutation = (
  options: UseMutationOptions<
    void,
    unknown,
    RearrangeFolderForCompanyVariables
  > = {},
) =>
  useMutation(
    ({ companyId, payload }) =>
      FilesAPI.rearrangeFoldersForCompany(companyId, payload),
    options,
  );

type DeleteFileGroupProps = {
  groupId: number;
  sourceType: string;
  sourceId: number;
};

export const useDeleteFileGroupMutation = (
  options: UseMutationOptions<void, unknown, DeleteFileGroupProps> = {},
) => {
  return useMutation(
    ({ groupId, sourceType, sourceId }) =>
      FilesAPI.deleteFileGroup(groupId, sourceType, sourceId),
    options,
  );
};

export const useUpdateFileGroupMutation = (
  options: UseMutationOptions<
    FileGroup,
    unknown,
    {
      formValues: UpdateGroupFormProps;
      groupId: number;
      sourceType: string;
      sourceId: number;
    }
  > = {},
) =>
  useMutation(
    ({ formValues, groupId, sourceType, sourceId }) =>
      FilesAPI.updateFolder(groupId, formValues, sourceType, sourceId),
    options,
  );

export const usePinFileMutation = (
  options: UseMutationOptions<void, unknown, number> = {},
) =>
  useMutation(
    (fileId: number) => FilesAPI.changeFilePinnedStatus(fileId, true),
    options,
  );
export const useUnpinFileMutation = (
  options: UseMutationOptions<void, unknown, number> = {},
) =>
  useMutation(
    (fileId: number) => FilesAPI.changeFilePinnedStatus(fileId, false),
    options,
  );
